import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Download, Share2, Sparkles } from 'lucide-react';
import { toPng } from 'html-to-image';

/**
 * Performance Card — shareable collectible card showing REAL on-chain + DCA data
 * Rarity scales with portfolio value:
 *   < $500    → Common (bronze)
 *   < $5,000  → Rare (cyan)
 *   < $25,000 → Legendary (gold)
 *   >= $25k   → Mythic (prismatic)
 */
const formatUsd = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatUsdShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};

const getRarity = (portfolioValue) => {
  if (portfolioValue >= 25000) {
    return {
      label: 'MYTHIC',
      tint: '#ff00aa',
      tint2: '#7c3aed',
      glow: 'rgba(255,0,170,0.55)',
      frame: 'linear-gradient(135deg, #00f4ff 0%, #ff00aa 33%, #ffd700 66%, #7c3aed 100%)',
      emoji: '👑',
    };
  }
  if (portfolioValue >= 5000) {
    return {
      label: 'LEGENDARY',
      tint: '#ffd700',
      tint2: '#ff00aa',
      glow: 'rgba(255,215,0,0.6)',
      frame: 'linear-gradient(135deg, #ffd700 0%, #ff00aa 50%, #7c3aed 100%)',
      emoji: '🚀',
    };
  }
  if (portfolioValue >= 500) {
    return {
      label: 'RARE',
      tint: '#00f4ff',
      tint2: '#12b7ff',
      glow: 'rgba(0,244,255,0.55)',
      frame: 'linear-gradient(135deg, #00f4ff 0%, #12b7ff 50%, #7c3aed 100%)',
      emoji: '💎',
    };
  }
  return {
    label: 'COMMON',
    tint: '#b87333',
    tint2: '#e8a87c',
    glow: 'rgba(184,115,51,0.55)',
    frame: 'linear-gradient(135deg, #b87333 0%, #e8a87c 50%, #b87333 100%)',
    emoji: '🌱',
  };
};

const PerformanceCard = ({
  user,
  totalValue,
  kap3 = { invested: 0, target: 0, withdrawn: 0, pending: 0, progressPct: 0 },
  dca = { invested: 0, btc: 0, value: 0, roi: 0 },
  wallets = { usdt: 0, bnb: 0 },
  aiTrading = { equity: 0, profit: 0, accounts: 0, winRate: 0 },
}) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0, scale: 1 });
  const [sheen, setSheen] = useState({ x: 50, y: 50 });
  const [downloading, setDownloading] = useState(false);

  const rarity = getRarity(totalValue);
  // Unique-ish serial from user_id
  const serial = (user?.user_id || 'anon').slice(-6).toUpperCase();

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setTilt({ x: -((y - cy) / cy) * 10, y: ((x - cx) / cx) * 10, scale: 1.02 });
    setSheen({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
  };
  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0, scale: 1 });
    setSheen({ x: 50, y: 50 });
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      setDownloading(true);
      setTilt({ x: 0, y: 0, scale: 1 });
      await new Promise((r) => setTimeout(r, 100));
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#020817',
      });
      const link = document.createElement('a');
      link.download = `sim360-performance-${rarity.label.toLowerCase()}-${serial}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error('Download error:', e);
    } finally {
      setDownloading(false);
    }
  };

  const handleWhatsApp = () => {
    const msg = `🚀 My Sim360° portfolio (${rarity.label})\n💰 Total: ${formatUsd(totalValue)}\n💎 KAP3: ${formatUsd(kap3.invested)} invested, ${kap3.progressPct.toFixed(1)}% to 2×\n🟠 DCA: ${dca.btc.toFixed(4)} BTC\n\n${window.location.origin}/myportafolio`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      className="rounded-2xl p-6"
      style={{
        background:
          'linear-gradient(180deg, rgba(11,28,56,0.65) 0%, rgba(4,8,22,0.85) 100%)',
        border: `1px solid ${rarity.tint}33`,
      }}
      data-testid="performance-card-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5" style={{ color: rarity.tint }} />
        <h3 className="text-base font-bold text-white">Your Performance Card</h3>
      </div>
      <p className="text-xs text-[#9EB4C0] mb-5">
        A collectible snapshot of your real portfolio — ready to share.
      </p>

      <div className="flex flex-col lg:flex-row items-center gap-8 justify-center">
        {/* Card */}
        <div style={{ perspective: '1200px' }}>
          <div
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="relative rounded-[22px] overflow-hidden transition-transform duration-150 ease-out select-none"
            style={{
              width: 340,
              height: 510,
              transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${tilt.scale})`,
              transformStyle: 'preserve-3d',
              boxShadow: `0 30px 80px -20px ${rarity.glow}`,
              background: rarity.frame,
              padding: 3,
            }}
            data-testid="performance-card-wrapper"
          >
            <div
              className="relative w-full h-full rounded-[19px] overflow-hidden"
              style={{
                background:
                  'radial-gradient(circle at 20% 10%, rgba(0,244,255,0.15), transparent 50%), radial-gradient(circle at 80% 90%, rgba(124,58,237,0.2), transparent 55%), linear-gradient(180deg, #0b1c38 0%, #020817 100%)',
              }}
            >
              {/* Sheen */}
              <div
                className="absolute inset-0 pointer-events-none mix-blend-screen"
                style={{
                  background: `radial-gradient(400px circle at ${sheen.x}% ${sheen.y}%, rgba(255,255,255,0.18), transparent 40%)`,
                  opacity: tilt.scale > 1 ? 1 : 0,
                }}
              />
              {/* Holographic stripes */}
              <div
                className="absolute inset-0 pointer-events-none opacity-[0.13] mix-blend-screen"
                style={{
                  backgroundImage: `repeating-linear-gradient(115deg, ${rarity.tint}40 0px, transparent 6px, transparent 14px, ${rarity.tint2}30 20px)`,
                }}
              />

              <div className="relative h-full flex flex-col p-5">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <div
                      className="text-[10px] tracking-[0.35em] font-bold"
                      style={{ color: rarity.tint }}
                    >
                      PERFORMANCE CARD
                    </div>
                    <div className="text-white font-black text-lg leading-none mt-1">SIM360°</div>
                  </div>
                  <div className="text-right" style={{ color: rarity.tint }}>
                    <div className="text-[9px] tracking-[0.3em] font-bold opacity-70">
                      {rarity.label}
                    </div>
                    <div className="text-[10px] font-mono opacity-80">#{serial}</div>
                  </div>
                </div>

                {/* Member name */}
                <div className="mt-3">
                  <div
                    className="text-[22px] font-black tracking-tight uppercase leading-none"
                    style={{
                      background: `linear-gradient(90deg, #ffffff 0%, ${rarity.tint} 100%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {(user?.name || 'Anonymous').split(' ')[0]}
                  </div>
                  <div className="text-[10px] text-white/60 uppercase tracking-[0.2em] mt-1">
                    Member
                  </div>
                </div>

                {/* Big number = portfolio total */}
                <div className="text-center mt-4 mb-3">
                  <div className="text-[10px] tracking-[0.25em] text-white/50 uppercase mb-1">
                    Total Portfolio Value
                  </div>
                  <div
                    className="text-4xl font-black"
                    style={{
                      color: rarity.tint,
                      textShadow: `0 0 18px ${rarity.glow}`,
                    }}
                  >
                    {formatUsdShort(totalValue)}
                  </div>
                </div>

                {/* Emoji centerpiece */}
                <div className="flex-1 flex items-center justify-center my-1">
                  <div
                    className="text-7xl"
                    style={{ filter: `drop-shadow(0 0 20px ${rarity.glow})` }}
                  >
                    {rarity.emoji}
                  </div>
                </div>

                {/* Stat bars (Pokemon-style) */}
                <div className="space-y-2 mt-2">
                  {[
                    {
                      label: 'AI TRADING',
                      value: aiTrading.equity,
                      meta: aiTrading.accounts > 0
                        ? `${aiTrading.accounts} acc · ${aiTrading.winRate.toFixed(0)}% WR`
                        : '—',
                      color: '#22c55e',
                    },
                    {
                      label: 'KAP3',
                      value: kap3.invested,
                      meta: `${kap3.progressPct.toFixed(0)}% → 2×`,
                      color: '#7c3aed',
                    },
                    {
                      label: 'DCA BTC',
                      value: dca.value,
                      meta: `${dca.btc.toFixed(4)} BTC`,
                      color: '#f7931a',
                    },
                    {
                      label: 'WALLETS',
                      value: wallets.usdt,
                      meta: `${wallets.bnb.toFixed(3)} BNB`,
                      color: '#12b7ff',
                    },
                  ].map((row) => {
                    const max = Math.max(aiTrading.equity, kap3.invested, dca.value, wallets.usdt, 1);
                    const w = (row.value / max) * 100;
                    return (
                      <div key={row.label} className="flex items-center gap-2">
                        <span className="text-[10px] font-bold w-16" style={{ color: row.color }}>
                          {row.label}
                        </span>
                        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${w}%`,
                              background: `linear-gradient(90deg, ${row.color} 0%, ${row.color}80 100%)`,
                              boxShadow: `0 0 6px ${row.color}`,
                            }}
                          />
                        </div>
                        <span
                          className="text-[10px] text-white/70 font-mono w-20 text-right truncate"
                          title={`${formatUsdShort(row.value)} · ${row.meta}`}
                        >
                          {formatUsdShort(row.value)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="mt-4 flex items-center justify-between text-[9px] text-white/50 uppercase tracking-[0.2em] border-t border-white/5 pt-2">
                  <span>The DCA Club</span>
                  <span>{new Date().toISOString().slice(0, 10)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action panel */}
        <div className="flex flex-col gap-3 lg:max-w-xs w-full">
          <p className="text-sm text-[#9EB4C0]">
            Your card upgrades automatically as your portfolio grows.
            Current rarity: <span style={{ color: rarity.tint }}>{rarity.label}</span>.
          </p>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-gradient-to-r from-[#00f4ff] to-[#12b7ff] text-[#040816] font-bold hover:shadow-lg hover:shadow-[#00f4ff]/40 transition-all"
            data-testid="performance-card-download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? 'Generating…' : 'Download PNG'}
          </Button>
          <Button
            onClick={handleWhatsApp}
            variant="outline"
            className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
            data-testid="performance-card-whatsapp-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <div className="text-[10px] text-[#556475] text-center mt-2 italic">
            Tier thresholds: Common &lt; $500 · Rare &lt; $5K · Legendary &lt; $25K · Mythic ≥ $25K
          </div>
        </div>
      </div>
    </section>
  );
};

export default PerformanceCard;
